import api from '@/api/axios';
import { localLogoutUser } from '@/api/services/user';
import { PrimaryButton } from '@/components/Button';
import { PREPARED_DIALOGS } from '@/components/ui/shared/Dialog/constants';
import { Text } from '@/components/Text';
import colors, { appTheme } from '@/config/colors';
import constants, { isPatientApp } from '@/config/constants';
import { useDialog } from '@/hooks/useDialog';
import { useLastEmail } from '@/hooks/useLastEmail';
import { AnonymousStackParamList } from '@/navigations/AnonymousStack';
import { navigate } from '@/navigations/RootNavigation';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import {
  clearLocalData,
  localLoginUser,
  refreshUserLoggedInState,
  serverLogoutUser,
  serverMultiLogoutUser,
} from '@/stores/auth/services';
import encryptedStorage from '@/stores/encryptedStorage';
import store, { useStoreActions } from '@/stores/store';
import { startCheckConsent } from '@/utils/consents';
import { logError } from '@/utils/logHandling';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Platform, Pressable, View } from 'react-native';
import { CheckBox } from 'react-native-elements';
import { LoginLayout } from './Layout';
import { MaxSessions } from './MaxSessions';
import { SessionsList } from '@/components/SharedComponents/SessionsList';
import { TwoFaDialog, TwoFaDialogRef } from './TwoFaDialog';
import { VerifyEmail } from './VerifyEmail';
import { getOrCreateDeviceToken } from '@/utils/secureDeviceToken';
import React, { useRef } from 'react';
import { User } from '@/stores/store.types';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { loginWithEpa } from '@/utils/epaAuth';
import { EpaAuthContext } from '@/containers/EpaAuthContextProvider';
import { SharedFormField } from '@/components/SharedComponents/FormField/SharedFormField';
import { Button } from '@/components/ui/therapist/Button';

interface LoginFormData {
  email: string;
  password: string;
  twoFAToken?: string;
  app?: string;
  deviceModel?: string;
  shortLivedSession?: boolean;
  deviceToken?: string;
  loginChannel?: string;
}

interface SigninPostResponse {
  user?: User;
  accessToken: string;
  credentials: any;
  message?: string;
}

interface FinalizeLoginData {
  accessToken: string;
  user?: User;
}

let timer2: NodeJS.Timeout | undefined;

const encryptedStore = encryptedStorage();

const rolesObject = {
  user: 'therapist',
  client: 'patient',
};

const wrongRoleContentText = isPatientApp
  ? 'Sie haben einen Therapeuten-Account verwendet. Die Anmeldung in der App für Patient:innen ist nur mit einem Patienten-Account möglich.'
  : 'Sie haben ein Patienten-Account angegeben. Die Anmeldung in dieser App ist nur mit einem Therapeuten-Account möglich.';

const titleText = isPatientApp
  ? 'Bitte geben Sie hier Ihre neolexon Patienten-Zugangsdaten ein.'
  : 'Bitte geben Sie hier die Zugangsdaten Ihres Therapeuten-Accounts ein.';

function Login(props: StackScreenProps<AnonymousStackParamList, 'Login'>) {
  const lastUsedEmail = useLastEmail();
  const loggedInNavigation =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();
  const { showDialog, removeAllDialogs } = useDialog();

  const setIsPatientDemo = useStoreActions(
    (actions) => actions.setIsPatientDemo,
  );
  const setIsTherapistDemo = useStoreActions(
    (actions) => actions.setIsTherapistDemo,
  );
  const setIsLoginScreen = useStoreActions(
    (actions) => actions.setIsLoginScreen,
  );
  const [stayLoggedIn, setStayLoggedIn] = useState(
    store.getState().stayLoggedIn,
  );

  const [epaError, setEpaError] = useState<string>();

  const authenticatePatient = useStoreActions(
    (actions) => actions.setIsAuthenticated,
  );
  const patientLogin = useStoreActions((actions) => actions.setUserData);

  const authContext = useContext(EpaAuthContext);

  const { control, handleSubmit, getValues, setValue } = useForm<LoginFormData>(
    {
      defaultValues: {
        email: lastUsedEmail,
        password: '',
        twoFAToken: '',
      },
      mode: 'onBlur',
    },
  );

  const wrongRoleDialog = () => {
    showDialog({
      title: 'Login fehlgeschlagen!',
      content: wrongRoleContentText,
    });
  };

  useEffect(() => {
    const focusListener = props.navigation.addListener('focus', () => {
      if (props.route.params?.email) {
        setValue('email', props.route.params?.email);
      }
    });

    return focusListener;
  }, [props.navigation, props.route.params]);

  useEffect(() => {
    if (store.getState().showLockPatternScreen) {
      store.getActions().setShowLockPatternScreen(false);
    }
    timer2 = setTimeout(async () => {
      await refreshUserLoggedInState();

      // When App is refreshed in browser user without optin should be logged out and all their private data is deleted from the device
      if (store.getState().isAuthenticated && store.getState().stayLoggedIn) {
        loggedInNavigation.navigate('Home');
      } else {
        if (
          store.getState().isAuthenticated &&
          !store.getState().stayLoggedIn
        ) {
          await serverLogoutUser({
            jwt: store.getState().signedInUser?.accessToken || '',
          });
          await localLogoutUser();
          await clearLocalData();
        }
      }
    }, 0);

    // Show account was deleted modal when the data for it is set to true
    setTimeout(async () => {
      const isAccountDeleted =
        await encryptedStore.getItem('accountWasDeleted');
      if (!isAccountDeleted) return;
      showDialog({
        title: 'Benutzerkonto gelöscht.',
        content: 'Ihr Benutzerkonto wurde erfolgreich gelöscht.',
        cancelable: false,
        confirmAction: {
          label: 'Weiter',
          onPress: async () => {
            await encryptedStore.removeItem('accountWasDeleted');
            removeAllDialogs();
          },
        },
      });
    }, 500);

    AsyncStorage.setItem(
      'stayLoggedIn',
      JSON.stringify(store.getState().stayLoggedIn),
    );

    if (Platform.OS === 'web') {
      const currentUrlParams = new URLSearchParams(window.location.search);
      if (currentUrlParams.get('demoStarten') === 'true') {
        if (constants.appMode === 'therapist') {
          handleDemoLogin(true);
        } else {
          handleDemoLogin(false);
        }
      }
    }

    return () => {
      clearTimeout(timer2);
    };
  }, []);

  const handleDemoLogin = async (isTherapist: boolean) => {
    let demoCredentials;
    if (isTherapist) {
      demoCredentials = {
        email: 'demotherapeut@neolexon.de',
        password: '0987654321',
      };
      setStayLoggedIn(false);
      setIsTherapistDemo(true);
    } else {
      demoCredentials = {
        email: 'demopatient@neolexon.de',
        password: '0987654321',
      };
      setStayLoggedIn(false);
      setIsPatientDemo(true);
    }

    // Call the onSubmit function
    await prepareLogin(demoCredentials);
  };

  const handleSubmit2FA = async (otp: string) => {
    const formState = getValues();
    setValue('twoFAToken', otp);
    prepareLogin({ ...formState, twoFAToken: otp });
  };

  const twoFaDialogRef = useRef<TwoFaDialogRef>(null);
  const isTwoFaDialogOpenRef = useRef(false);

  const prepareLogin = async (data: LoginFormData) => {
    let stayLoggedInUser = stayLoggedIn;

    const isDemoMode =
      data.email === 'demotherapeut@neolexon.de' ||
      data.email === 'demopatient@neolexon.de';
    if (isDemoMode) {
      stayLoggedInUser = false;
    }
    data.app = Platform.OS === 'web' ? 'browser' : Platform.OS;

    if (Platform.OS === 'ios') {
      data.deviceModel = 'ios-device';
    }
    if (Platform.OS === 'android') {
      data.deviceModel = Platform?.constants.Model ?? 'android-device'; //Only on andorid
    }

    if (!data.deviceModel) {
      data.deviceModel = 'unknown-device';
    }

    if (Platform.OS === 'web' && !isPatientApp) {
      if (window.location.href.indexOf('nur-eigentraining-einstellen') > -1) {
        data.app = 'web';
        data.deviceModel = 'Browser (nur Eigentraining einstellen)';
        store.getActions().setTherapistWebAppLogin(true);
      } else {
        store.getActions().setTherapistWebAppLogin(false);
      }
    }

    // * Adding the 'shortLivedSession' for the server to know about the sessions
    data.shortLivedSession = !stayLoggedInUser;
    data.email = data.email.trim();
    if (!constants.legacyBackendCompatibility) {
      data.deviceToken = await getOrCreateDeviceToken();
      data.loginChannel = isPatientApp
        ? 'neolexon-aphasie'
        : 'neolexon-therapeut';
    } else {
      delete data.deviceToken;
      delete data.loginChannel;
      delete data.twoFAToken;
    }

    store.getActions().setShowLoadingSpinner(true);

    api
      .post<SigninPostResponse>('/api/auth/signin', data)
      .then(async ({ data: responseData }) => {
        if (isDemoMode) return finalizeLogin(responseData, true);
        /**
         * roles do not match the app mode
         */
        const nonPatientUserInPatientApp =
          constants.appName === 'Aphasie' &&
          !responseData?.user?.roles?.includes('client');

        const rolesDoNotMatch =
          responseData.user?.roles &&
          constants.appMode !== rolesObject[responseData.user.roles[0]];

        if (nonPatientUserInPatientApp || rolesDoNotMatch)
          return wrongRoleDialog();

        /**
         * Login as therapist and max sessions reached
         */
        if (
          responseData?.credentials?.length > 0 &&
          !responseData?.accessToken &&
          !isPatientApp
        ) {
          return showDialog({
            title:
              'Sie haben die maximale Anzahl eingeloggter Geräte erreicht. Um sich auf diesem Gerät anmelden zu können, melden Sie sich auf einem anderen Gerät ab.',
            contentComponent: () =>
              useMemo(
                () => (
                  <MaxSessions>
                    <SessionsList sessions={[...responseData.credentials]} />
                  </MaxSessions>
                ),
                [responseData.credentials],
              ),
            confirmAction: {
              label: 'Erneut versuchen',
              onPress: () => {
                const formState = getValues();
                prepareLogin(formState);
                removeAllDialogs();
              },
            },
          });
        }
        /**
         * Is patient and 2fa is activated
         */
        if (
          typeof responseData === 'string' &&
          responseData === 'Need 2FA Token'
        ) {
          if (!isTwoFaDialogOpenRef.current) {
            isTwoFaDialogOpenRef.current = true;
            return showDialog({
              title: 'Überprüfungscode eingeben',
              contentComponent: () => (
                <TwoFaDialog
                  ref={twoFaDialogRef}
                  onSubmit={handleSubmit2FA}
                  onPressSendCodeAgain={() => {
                    setValue('twoFAToken', '');
                    twoFaDialogRef.current?.resetOTP();
                    prepareLogin(data);
                  }}
                />
              ),
              cancelAction: {
                label: 'Abbrechen',
                onPress: () => {
                  isTwoFaDialogOpenRef.current = false;
                  handlePressCancel2FA();
                },
              },
            });
          } else {
            twoFaDialogRef.current?.resetOTP();
            return;
          }
        }
        /**
         * is patient and is logged in on another device
         */
        if (
          responseData.credentials &&
          responseData?.credentials?.length > 0 &&
          !responseData.accessToken
        ) {
          return showDialog({
            title: 'Sie sind bereits auf einem anderem Gerät eingeloggt',
            content:
              'Wenn Sie sich auf diesem Gerät anmelden, werden Sie auf dem anderen Gerät abgemeldet.',
            confirmAction: {
              label: 'Weiter',
              onPress: async () => {
                const formState = getValues();
                await serverMultiLogoutUser(responseData.credentials);
                prepareLogin(formState);
                removeAllDialogs();
              },
            },
          });
        }

        /**
         * server response is not an object might inidcate the user is not verified
         */
        if (
          (!constants.legacyBackendCompatibility &&
            responseData?.message === 'needs validation') ||
          (constants.legacyBackendCompatibility &&
            typeof responseData !== 'object')
        ) {
          return showDialog({
            title: 'Login fehlgeschlagen',
            contentComponent: () => <VerifyEmail />,
            cancelable: false,
            cancelOnBackdropPress: true,
          });
        }

        removeAllDialogs();
        if (stayLoggedInUser) {
          store.getActions().setLastLoginEmail(data.email);
        }
        setValue('password', '');
        setValue('twoFAToken', '');
        setIsLoginScreen(false);

        if (!stayLoggedInUser) await encryptedStore.clear();
        store.getActions().setStayLoggedIn(stayLoggedInUser);
        await AsyncStorage.setItem(
          'stayLoggedIn',
          JSON.stringify(stayLoggedInUser),
        );
        if (!responseData.user) return;
        finalizeLogin(responseData);
      })
      .catch((error) => {
        store.getActions().setShowLoadingSpinner(false);
        logError('server returned an error', error);

        /**
         * There is no network conenection to the server
         */
        if (error?.code === 'ERR_NETWORK')
          return showDialog(PREPARED_DIALOGS.noInternetDialog());

        const errorResponseData = error?.response?.data;

        /**
         * The patient account has been deactivated and needs reactivation
         */
        if (errorResponseData?.code === 'error.auth.accountDeactivated')
          return navigate('ReactivateAccount', {
            reactivationToken: error.response.data.reactivationToken,
          });

        /**
         * The login credentials are wrong
         */
        if (
          errorResponseData?.message === 'Email oder Passwort stimmten nicht!'
        )
          return showDialog({
            title: 'Login fehlgeschlagen',
            danger: true,
            content:
              'Ihr Passwort oder Ihre E-Mail-Adresse sind falsch. Oder Sie haben sich noch nicht bei neolexon registriert!',
            cancelable: false,
          });

        /**
         * The 2FA token is invalid or has already expired
         */
        if (
          ['2FA Token expired', 'Invalid 2FA Token'].includes(
            errorResponseData?.message,
          )
        ) {
          twoFaDialogRef.current?.resetOTP();
          // Update the dialog’s error state
          twoFaDialogRef.current?.updateError(
            errorResponseData?.message === '2FA Token expired'
              ? 'expired'
              : 'invalid',
          );
          return;
        }

        /**
         * The user is not allowed to login via this app
         */
        if (
          errorResponseData?.message === 'Login via this app is not allowed'
        ) {
          return showDialog({
            title: 'Login nicht möglich',
            danger: true,
            content:
              'Ihr Account ist für die Anmeldung in dieser App nicht freigeschaltet. Erstellen Sie einen neuen Account oder wenden Sie sich an den Support.',
            cancelable: false,
          });
        }

        /**
         * Something else went wrong
         */
        return showDialog({
          title: 'Login fehlgeschlagen',
          danger: true,
          content:
            'Beim Anmelden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.',
          cancelable: false,
        });
      });
  };

  async function finalizeLogin(
    loginData: FinalizeLoginData,
    isDemoMode = false,
  ) {
    /**
     * Do the login for either therapist or patient
     */
    if (!loginData.user) return;
    if (constants.appMode === 'therapist') {
      await localLoginUser({
        accessToken: loginData.accessToken,
        user: {
          _id: loginData.user._id,
          email: loginData.user.email,
          code: loginData.user.code,
          firstName: loginData.user.firstName,
          lastName: loginData.user.lastName,
          appStartResumeLock: loginData.user.appStartResumeLock,
          eigentrainingLock: loginData.user.eigentrainingLock,
          appLockPattern: loginData.user.appLockPattern,
        },
      });
    } else {
      authenticatePatient(true);
      patientLogin({
        accessToken: loginData.accessToken,
        user: {
          _id: loginData.user._id,
          email: loginData.user.email,
          code: loginData.user.code,
          firstName: loginData.user.firstName,
          lastName: loginData.user.lastName,
          digaUser: loginData.user.digaUser,
          therapistCanSeeResults: loginData.user.therapistCanSeeResults,
          idpInsuranceId: loginData.user.idpInsuranceId,
          userEpa: loginData.user.userEpa,
        },
      });
    }

    /**
     * Initialize the results and practice session
     */
    store
      .getActions()
      .initializeResultsWithKeyAndPracticeSessionWithKey(undefined);
    store.getActions().setShowLoadingSpinner(false);

    /**
     * If is demo mode, do not check for consent
     */
    if (isDemoMode) return;
    startCheckConsent();
  }

  function handlePressCancel2FA() {
    setValue('twoFAToken', '');
    setValue('password', '');
    removeAllDialogs();
  }

  const handlePressStayLoggedInConfirm = () => {
    const formData = getValues();
    if (!formData) return;
    removeAllDialogs();
    prepareLogin(formData); // Proceed with login using temporarily stored form data
  };

  const onSubmit = async (data: LoginFormData) => {
    // Check if stayLoggedIn is false, show modal and wait for user action
    if (stayLoggedIn) return prepareLogin(data);
    showDialog({
      title: 'Hinweis',
      content:
        'Sie haben sich entschieden, nicht auf diesem Gerät angemeldet zu bleiben. Das bedeutet, dass Sie sich bei jedem Start der App erneut anmelden und die Übungssets erneut herunterladen müssen.',
      confirmAction: {
        label: 'Weiter',
        onPress: handlePressStayLoggedInConfirm,
      },
    });
  };

  useEffect(() => {
    const currentEmail = getValues().email;
    if (currentEmail === lastUsedEmail) return;
    setValue('email', lastUsedEmail);
  }, [lastUsedEmail]);
  return (
    <LoginLayout>
      <View>
        <View className="pb-6">
          <Text className="text-xl">{titleText}</Text>
        </View>
        <View className="gap-y-2">
          <SharedFormField
            control={control}
            name="email"
            label="E-Mail-Adresse"
            useEmailInput
            rules={{
              required: 'Pflichtfeld',
              pattern: {
                value:
                  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/gi,
                message: 'E-Mail-Adresse ungültig',
              },
            }}
          />
          <View>
            <SharedFormField
              control={control}
              name="password"
              label="Passwort"
              rules={{ required: 'Pflichtfeld' }}
              secureTextEntry
              usePasswordInput
            />
            <View className="items-end">
              <Pressable
                className="w-fit p-2"
                onPress={() => {
                  props.navigation.navigate('ResetPassword', {
                    email: getValues().email,
                  });
                }}
              >
                <Text style={{ fontSize: 16, paddingTop: 2 }}>
                  Passwort vergessen?
                </Text>
              </Pressable>
            </View>
          </View>
        </View>

        <View className="flex-1 gap-y-4 mt-2">
          <CheckBox
            wrapperStyle={{
              alignItems: 'flex-start',
              padding: 0,
              margin: 0,
            }}
            size={28}
            containerStyle={{
              backgroundColor: 'transparent',
              borderWidth: 0,
              padding: 0,
              margin: 0,
            }}
            checkedColor={appTheme.primary}
            checked={stayLoggedIn}
            onPress={() => setStayLoggedIn((p) => !p)}
            title={
              <View className="left-1">
                <Text
                  style={{
                    fontFamily: isPatientApp ? '' : 'InterSemiBold',
                    fontSize: 18,
                    fontWeight: 600,
                    marginBottom: 1,
                  }}
                >
                  Auf diesem Gerät angemeldet bleiben.
                </Text>
                <Text className="w-2/3">
                  Dies sollte nur auf eigenen Geräten aktiviert werden, um
                  unbefugten Zugriff von Personen zu verhindern, die das Gerät
                  nach Ihnen nutzen.
                </Text>
              </View>
            }
          />
          <View>
            <View className="flex-row gap-4 ">
              <View className="flex-1">
                {isPatientApp ? (
                  <PrimaryButton
                    id="login-anmelden"
                    onPress={handleSubmit(onSubmit)}
                    label="Anmelden"
                  />
                ) : (
                  <Button
                    style={{ width: '100%', justifyContent: 'center' }}
                    id="login-anmelden"
                    onPress={handleSubmit(onSubmit)}
                    label="Anmelden"
                  />
                )}
              </View>
              <View className="flex-1">
                {isPatientApp ? (
                  <PrimaryButton
                    look="neutral"
                    onPress={() => {
                      if (constants.appMode === 'therapist') {
                        handleDemoLogin(true);
                      } else {
                        handleDemoLogin(false);
                      }
                    }}
                    label="Demo"
                  />
                ) : (
                  <Button
                    look="secondary"
                    label="Demo"
                    style={{ width: '100%', justifyContent: 'center' }}
                    onPress={() => {
                      if (constants.appMode === 'therapist') {
                        handleDemoLogin(true);
                      } else {
                        handleDemoLogin(false);
                      }
                    }}
                  />
                )}
              </View>
            </View>
          </View>
          <View className="flex-1 mt-2">
            <Pressable
              onPress={() => {
                props.navigation.navigate('SignUpController');
              }}
            >
              <Text
                className="bold text-center mt-2 text-base"
                style={{ fontFamily: isPatientApp ? '' : 'InterSemiBold' }}
              >
                Noch kein Benutzerkonto?{' '}
                <Text className="text-green-neolexon">Jetzt registrieren</Text>
              </Text>
            </Pressable>
            {constants.epaEnabled && isPatientApp && (
              <View>
                <PressableNeolexon
                  style={{
                    backgroundColor: appTheme.primary,
                    width: '100%',
                    borderRadius: 8,
                    padding: 15,
                    marginTop: 10,
                  }}
                  onPress={async () => {
                    await loginWithEpa(authContext, () => {
                      setEpaError(
                        'Es wurde kein Account gefunden, der mit diesen Krankenkassendaten verknüpft ist. Bitte melden Sie sich in Ihrem bestehenden Neolexon-Account an und verknüpfen Sie diesen mit der ePA (elektronische Patientenaktie).',
                      );
                    });
                  }}
                >
                  <Text
                    style={{
                      color: colors.white,
                      textAlign: 'center',
                      fontWeight: '700',
                    }}
                  >
                    Mit GesundheitsID anmelden
                  </Text>
                </PressableNeolexon>
                {epaError && (
                  <Text className="text-center text-base mt-4 text-red-500">
                    {epaError}
                  </Text>
                )}
              </View>
            )}
          </View>
        </View>
      </View>
    </LoginLayout>
  );
}

export default Login;
